import { useState, useEffect } from "react"
import { cn } from "@/lib/utils"
import { withIva } from "@/lib/tax"
import { useParams, useNavigate } from "react-router-dom"
import Header from "@/components/Header"
import Footer from "@/components/Footer"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  ShoppingCart,
  ChevronLeft,
  Download,
  Info,
  Package,
  Weight,
  CheckCircle2,
  PhoneCall,
  Loader2,
  Send,
} from "lucide-react"
import { toast } from "@/lib/toast"
import { useProduct } from "@/hooks/useProducts"
import { useCart } from "@/hooks/useCart"
import { apiClient } from "@/api/client"
import { useLanguage } from "@/hooks/useLanguage"
import {
  formatPricePerWeightUnit,
  formatTotalWeight,
  getPricePerWeightUnit,
} from "@/lib/productDisplay"
import { SEO } from "@/components/SEO"
import { useAuth } from "@/hooks/useAuth"
import LoginModal from "@/components/auth/LoginModal"

const ProductDetail = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t, language } = useLanguage()
  const { user } = useAuth()

  const productId = id ? parseInt(id) : undefined
  const { data: product, isLoading, error } = useProduct(productId)
  const { addItem } = useCart()

  const [internalSelectedImage, setInternalSelectedImage] = useState<string | null>(null)
  const [isLoginOpen, setIsLoginOpen] = useState(false)
  const [isInquiryOpen, setIsInquiryOpen] = useState(false)
  const [openInquiryAfterLogin, setOpenInquiryAfterLogin] = useState(false)
  const [inquiryPhone, setInquiryPhone] = useState("")
  const [inquiryMessage, setInquiryMessage] = useState("")
  const [isSendingInquiry, setIsSendingInquiry] = useState(false)

  // Derived image: use user selection if available, otherwise fallback to product primary image
  const selectedImage =
    internalSelectedImage ||
    (product?.primaryImageUrl
      ? apiClient.ensureAbsUrl(product.primaryImageUrl)
      : product?.imageUrls && product.imageUrls.length > 0
        ? apiClient.ensureAbsUrl(product.imageUrls[0])
        : null) ||
    null

  const text = t.productDetail

  useEffect(() => {
    if (user && openInquiryAfterLogin) {
      setIsLoginOpen(false)
      setIsInquiryOpen(true)
      setOpenInquiryAfterLogin(false)
    }
  }, [openInquiryAfterLogin, user])

  const handleAddToCart = () => {
    if (product) {
      addItem(product)
      toast.success(`${product.name} ${text.addedToCart}`)
    }
  }

  const handleContact = () => {
    if (!product) return

    if (!user) {
      setOpenInquiryAfterLogin(true)
      setIsLoginOpen(true)
      return
    }

    setIsInquiryOpen(true)
  }

  const handleSendInquiry = async () => {
    if (!product) return

    if (inquiryPhone.trim().length < 10) {
      toast.error(text.inquiryPhoneError)
      return
    }

    if (inquiryMessage.trim().length < 10) {
      toast.error(text.inquiryMessageError)
      return
    }

    setIsSendingInquiry(true)
    try {
      await apiClient.sendProductInquiry({
        productId: product.id,
        phone: inquiryPhone.trim(),
        message: inquiryMessage.trim(),
      })
      toast.success(text.inquirySuccess)
      setIsInquiryOpen(false)
      setInquiryMessage("")
    } catch (error: any) {
      if (error.status === 401) {
        setOpenInquiryAfterLogin(true)
        setIsInquiryOpen(false)
        setIsLoginOpen(true)
        toast.error(text.loginRequired)
      } else if (error.status === 429) {
        toast.error(text.inquiryRateLimit)
      } else {
        toast.error(text.inquiryError)
      }
    } finally {
      setIsSendingInquiry(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <div className="flex animate-pulse flex-col items-center">
            <div className="mb-4 h-12 w-12 rounded-full bg-primary/20"></div>
            <p className="text-muted-foreground">{text.loading}</p>
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  if (error || !product) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <Header />
        <div className="flex flex-1 flex-col items-center justify-center p-4">
          <Info className="mb-4 h-12 w-12 text-destructive" />
          <h2 className="mb-2 text-2xl font-bold">{error ? text.error : text.notFound}</h2>
          <Button onClick={() => navigate(-1)} variant="outline">
            <ChevronLeft className="mr-2 h-4 w-4" />
            {text.back}
          </Button>
        </div>
        <Footer />
      </div>
    )
  }

  const allImages = (
    product.imageUrls && product.imageUrls.length > 0
      ? product.imageUrls
      : product.imageUrl
        ? [product.imageUrl]
        : []
  )
    .map((url) => apiClient.ensureAbsUrl(url))
    .filter((url): url is string => !!url)

  const technicalSpecificationUrl = apiClient.ensureAbsUrl(product.technicalSpecificationUrl)

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <SEO
        title={product ? `${product.name} | MeatBid México` : t.seo.defaultTitle}
        description={product?.description || t.seo.defaultDesc}
        image={selectedImage || undefined}
      />
      <Header />

      <main className="flex-1 pb-20 pt-24">
        <div className="container mx-auto px-4">
          <Button
            variant="ghost"
            className="mb-8 flex items-center p-0 text-muted-foreground transition-colors hover:bg-transparent hover:text-primary"
            onClick={() => navigate(-1)}
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            {text.back}
          </Button>

          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
            {/* Image Gallery */}
            <div className="space-y-4">
              <div className="group aspect-square overflow-hidden rounded-2xl border border-border/50 bg-muted">
                {selectedImage ? (
                  <img
                    src={selectedImage}
                    alt={product.name}
                    decoding="async"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                    <Package className="h-20 w-20 opacity-20" />
                  </div>
                )}
              </div>

              {allImages.length > 1 && (
                <div className="grid grid-cols-4 gap-3 sm:grid-cols-5 md:grid-cols-6">
                  {allImages.map((img, idx) => (
                    <Button
                      key={idx}
                      variant="ghost"
                      onClick={() => setInternalSelectedImage(img)}
                      className={cn(
                        "aspect-square h-auto overflow-hidden rounded-lg border-2 p-0 transition-all",
                        selectedImage === img
                          ? "border-primary ring-2 ring-primary/20"
                          : "border-transparent hover:border-muted-foreground/30"
                      )}
                    >
                      <img
                        src={img}
                        alt={`${product.name} ${idx + 1}`}
                        loading="lazy"
                        decoding="async"
                        className="h-full w-full object-cover"
                      />
                    </Button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="flex flex-col">
              <div className="mb-6">
                <div className="mb-4 flex flex-wrap gap-2">
                  {product.categoryName && (
                    <Badge
                      variant="secondary"
                      className="border-primary/10 bg-primary/5 px-3 py-1 text-primary"
                    >
                      {language === "es"
                        ? product.categoryName
                        : (product as any).categoryNameEn || product.categoryName}
                    </Badge>
                  )}
                  <Badge
                    variant="outline"
                    className={`px-3 py-1 ${product.stockQuantity > 0 ? "border-green-200 bg-green-50 text-green-600" : "border-red-200 bg-red-50 text-red-600"}`}
                  >
                    <CheckCircle2 className="mr-1 h-3 w-3" />
                    {product.stockQuantity > 0 ? text.inStock : text.outOfStock}
                  </Badge>
                </div>

                <h1 className="mb-4 font-display text-3xl tracking-tighter text-foreground md:text-4xl lg:text-5xl">
                  {product.name}
                </h1>

                <div className="mb-6 flex flex-col gap-1 font-display">
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-black text-foreground md:text-5xl">
                      {formatPricePerWeightUnit(
                        getPricePerWeightUnit(product),
                        product.weightUnit,
                        { withTax: !!product.isTaxable, isTaxable: !!product.isTaxable }
                      )}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm uppercase text-muted-foreground">
                      {t.units.soldIn} {product.weight} {product.weightUnit} — ${product.isTaxable ? withIva(product.price).toFixed(2) : product.price}/{t.units.perUnit}
                    </span>
                    {product.isTaxable && (
                      <Badge
                        variant="outline"
                        className="h-4 rounded-none border-primary/20 bg-primary/5 px-1.5 py-0 text-xs uppercase text-primary"
                      >
                        {text.ivaLabel}
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="mb-8 grid grid-cols-2 gap-4">
                  <div className="rounded-none border border-border/50 bg-muted/30 p-4">
                    <div className="mb-1 flex items-center font-display text-xs tracking-widest text-muted-foreground">
                      <Weight className="mr-1.5 h-3.5 w-3.5" />
                      {text.weight}
                    </div>
                    <div className="font-display text-lg tracking-wider">
                      {product.weight} {product.weightUnit}
                    </div>
                  </div>
                  <div className="rounded-none border border-border/50 bg-muted/30 p-4">
                    <div className="mb-1 flex items-center font-display text-xs tracking-widest text-muted-foreground">
                      <Package className="mr-1.5 h-3.5 w-3.5" />
                      {text.stock}
                    </div>
                    <div className="font-display text-lg tracking-wider">
                      {product.stockQuantity} {text.units}
                    </div>
                    {product.weight && product.weight > 0 && (
                      <div className="mt-1 text-xs font-bold uppercase tracking-widest text-muted-foreground/70">
                        {formatTotalWeight(product.stockQuantity || 0, product.weight, product.weightUnit, t.units.total)}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex flex-col gap-4 sm:flex-row">
                  <Button
                    onClick={handleAddToCart}
                    size="lg"
                    disabled={product.stockQuantity <= 0}
                    className="h-14 flex-1 rounded-none bg-action font-display text-lg tracking-label text-action-foreground shadow-lg transition-all duration-500 ease-out-expo hover:scale-[1.02] hover:bg-action/90 active:scale-95"
                  >
                    <ShoppingCart className="mr-2 h-5 w-5" />
                    {text.addToCart}
                  </Button>
                  <Button
                    onClick={handleContact}
                    size="lg"
                    variant="outline"
                    className="h-14 flex-1 rounded-none border-2 font-display text-lg tracking-widest"
                  >
                    <PhoneCall className="mr-2 h-5 w-5" />
                    {text.contact}
                  </Button>
                </div>
              </div>

              <Separator className="my-8" />

              <div className="space-y-8">
                <div>
                  <h3 className="mb-4 flex items-center font-display text-xl uppercase tracking-widest">
                    <Info className="mr-2 h-5 w-5 text-primary" />
                    {text.description}
                  </h3>
                  <p className="whitespace-pre-wrap leading-relaxed text-muted-foreground">
                    {product.description || text.noDescription}
                  </p>
                </div>

                {technicalSpecificationUrl && (
                  <div>
                    <h3 className="mb-4 flex items-center font-display text-xl uppercase tracking-widest">
                      <Download className="mr-2 h-5 w-5 text-primary" />
                      {text.specifications}
                    </h3>
                    <Button
                      asChild
                      variant="outline"
                      className="w-full rounded-none border-2 border-dashed font-display tracking-widest transition-all hover:border-primary hover:bg-primary/5 sm:w-auto"
                    >
                      <a href={technicalSpecificationUrl} target="_blank" rel="noopener noreferrer">
                        <Download className="mr-2 h-4 w-4" />
                        {text.downloadSpec}
                      </a>
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      <LoginModal
        isOpen={isLoginOpen}
        onClose={() => {
          setIsLoginOpen(false)
          setOpenInquiryAfterLogin(false)
        }}
        onSuccess={() => {
          setIsLoginOpen(false)
          setIsInquiryOpen(true)
          setOpenInquiryAfterLogin(false)
        }}
      />

      <Dialog open={isInquiryOpen} onOpenChange={setIsInquiryOpen}>
        <DialogContent className="overflow-hidden rounded-none border-border bg-card p-0 sm:max-w-[520px]">
          <div className="border-b border-border bg-muted/40 p-6">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl uppercase tracking-widest">
                {text.inquiryTitle}
              </DialogTitle>
              <DialogDescription>{text.inquiryDescription}</DialogDescription>
            </DialogHeader>
          </div>

          <div className="space-y-5 p-6">
            <div className="border border-border/70 bg-muted/20 p-4">
              <p className="font-display text-lg uppercase tracking-widest text-foreground">
                {product.name}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                {formatPricePerWeightUnit(
                  getPricePerWeightUnit(product),
                  product.weightUnit,
                  { withTax: !!product.isTaxable, isTaxable: !!product.isTaxable }
                )}
                {" "}· {product.weight} {product.weightUnit}/{t.units.perUnit}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="inquiry-phone" className="text-xs font-bold uppercase tracking-widest">
                {text.inquiryPhone}
              </Label>
              <Input
                id="inquiry-phone"
                type="tel"
                value={inquiryPhone}
                onChange={(event) => setInquiryPhone(event.target.value)}
                placeholder={text.inquiryPhonePlaceholder}
                className="rounded-none"
              />
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="inquiry-message"
                className="text-xs font-bold uppercase tracking-widest"
              >
                {text.inquiryMessage}
              </Label>
              <Textarea
                id="inquiry-message"
                rows={5}
                value={inquiryMessage}
                onChange={(event) => setInquiryMessage(event.target.value)}
                placeholder={text.inquiryPlaceholder}
                className="rounded-none"
              />
            </div>
          </div>

          <DialogFooter className="border-t border-border bg-muted/20 p-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsInquiryOpen(false)}
              disabled={isSendingInquiry}
              className="rounded-none"
            >
              {text.inquiryCancel}
            </Button>
            <Button
              type="button"
              onClick={handleSendInquiry}
              disabled={isSendingInquiry}
              className="rounded-none bg-action text-action-foreground hover:bg-action/90"
            >
              {isSendingInquiry ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Send className="mr-2 h-4 w-4" />
              )}
              {text.inquirySend}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  )
}

export default ProductDetail

